import { authHeaders, fetchWithAuthRetry, fetchWithTimeout, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export type DocumentMember = {
  id: string;
  nickname: string;
  email: string;
};

export type DocumentVisibility = 'PROJECT' | 'PRIVATE';

export type Document = {
  id: string;
  projectId: string;
  title: string;
  body: string;
  bodyTiptap: string;
  contentTokenCount?: number | null;
  tags: string[];
  // AI 추천 태그(미확정). 트리/목록필터/검색에는 포함되지 않으며 사용자가 승격하면 tags로 이동한다.
  suggestedTags: string[];
  visibility: DocumentVisibility;
  createdById: string | null;
  createdByAgentId?: string | null;
  createdByType?: string | null;
  createdByCredentialKind?: string | null;
  createdByAgentName?: string | null;
  ownerMemberId: string;
  createdBy: DocumentMember | null;
  archivedAt: string | null;
  archivedByMemberId: string | null;
  // 요청 멤버 기준 개인 즐겨찾기(별표) 상태. 다른 멤버의 별표와 독립적이다.
  isFavorite: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  webUrl: string | null;
};

export type DocumentShareStatus = {
  isActive: boolean;
  token: string | null;
  url: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type DocumentAccessGrantSource = 'MANUAL' | 'ACCESS_REQUEST' | 'RUNNER_RUN';

type DocumentAccessGrantActor = {
  id: string;
  nickname: string;
};

// 비공개 문서의 특정 멤버 열람 권한. 열람 전용이라 등급 필드가 없다.
export type DocumentAccessGrant = {
  id: string;
  documentId: string;
  memberId: string;
  member: DocumentMember;
  grantedByMemberId: string;
  grantedByMember: DocumentAccessGrantActor;
  source: DocumentAccessGrantSource;
  requestId: string | null;
  createdAt: string;
  revokedAt: string | null;
  revokedByMemberId: string | null;
  revokedByMember: DocumentAccessGrantActor | null;
};

export type DocumentPlanLink = {
  id: string;
  planId: string;
  documentId: string;
  title: string;
  status: string;
  type: string | null;
  priority: string;
  note: string | null;
  createdAt: string;
};

export type PublicDocumentAttachment = {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  downloadUrl: string | null;
  expiresInSeconds: number | null;
};

export type PublicSharedDocument = {
  id: string;
  projectId: string;
  title: string;
  body: string;
  bodyTiptap: string;
  tags: string[];
  visibility: DocumentVisibility;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    nickname: string;
  };
  attachments: PublicDocumentAttachment[];
};

export type DocumentListItem = {
  id: string;
  projectId: string;
  title: string;
  bodyPreview?: string;
  contentTokenCount?: number | null;
  tags: string[];
  suggestedTags: string[];
  visibility: DocumentVisibility;
  createdById: string | null;
  createdByAgentId?: string | null;
  createdByType?: string | null;
  createdByCredentialKind?: string | null;
  createdByAgentName?: string | null;
  ownerMemberId: string;
  createdBy: DocumentMember | null;
  archivedAt: string | null;
  archivedByMemberId: string | null;
  // 요청 멤버 기준 개인 즐겨찾기(별표) 상태. 다른 멤버의 별표와 독립적이다.
  isFavorite: boolean;
  // 요청 멤버가 소유하면서 활성 공유가 있는지.
  isSharedByMe: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  webUrl: string | null;
};

export type DocumentArchivedFilter = 'ACTIVE' | 'ARCHIVED' | 'ALL';

export type DocumentQueryParams = {
  search?: string;
  createdByMemberId?: string;
  tags?: string[];
  // 태그 트리(Bear 방식): 상위 노드 경로. 자기 자신과 하위 경로(`prefix/...`) 태그 문서를 포함한다.
  tagPrefix?: string;
  // 태그 트리의 '태그 없음' 노드: 태그가 없는 문서만 조회한다.
  untagged?: boolean;
  // 요청 멤버의 즐겨찾기(별표) 문서만 조회한다. 태그 메타 필터와 상호 배타적이다.
  favorite?: boolean;
  // 요청 멤버가 소유하면서 활성 공유가 있는 문서만 조회한다. 태그 메타 필터와 상호 배타적이다.
  sharedByMe?: boolean;
  visibility?: DocumentVisibility;
  archived?: DocumentArchivedFilter;
  page?: number;
  pageSize?: number;
};

export type DocumentTagAggregate = {
  tag: string;
  count: number;
};

export type DocumentTagsQueryParams = {
  visibility?: DocumentVisibility;
  archived?: DocumentArchivedFilter;
  // 문서함 작성자 필터 값. 태그 정리 후보 조회에는 넘기지 않는다.
  createdByMemberId?: string;
};

export type DocumentTagMergeSuggestion = {
  from: string;
  to: string;
  fromCount: number;
  toCount: number;
  reason: 'CASING' | 'SIMILAR';
};

export type DocumentTagSuggestions = {
  nearDuplicates: DocumentTagMergeSuggestion[];
  lowUsage: DocumentTagAggregate[];
};

export type RenameDocumentTagInput = {
  from: string;
  to: string;
  includeDescendants?: boolean;
};

// LLM 기반 relabel 제안 1건. affectedCount는 이 변환이 영향을 주는 문서 수(하위 계층 포함).
export type DocumentTagRelabelSuggestion = {
  from: string;
  to: string;
  reason: string;
  affectedCount: number;
};

// apply 배치에 담는 op(사람이 수락한 제안). affectedCount는 서버가 재계산하므로 보내지 않는다.
export type DocumentTagRelabelApplyOp = {
  from: string;
  to: string;
  reason: string;
};

export type DocumentTagRelabelApplyResult = DocumentTagRelabelApplyOp & {
  updatedCount: number;
};

export type DocumentTagRelabelApplyResponse = {
  batchId: string;
  results: DocumentTagRelabelApplyResult[];
};

export type CreateDocumentInput = {
  title: string;
  body: string;
  tags?: string[];
  visibility?: DocumentVisibility;
};

export type UpdateDocumentInput = {
  title?: string;
  body?: string;
  tags?: string[];
  visibility?: DocumentVisibility;
};

export type DocumentRevision = {
  id: string;
  documentId: string;
  revisionNumber: number;
  title: string;
  bodyPreview?: string;
  tags: string[];
  visibility: DocumentVisibility;
  createdById: string;
  createdByAgentId?: string | null;
  createdByCredentialKind?: string | null;
  createdByAgentName?: string | null;
  createdBy: DocumentMember;
  createdAt: string;
};

export type DocumentRevisionDetail = DocumentRevision & {
  body: string;
  bodyTiptap: string;
};

type DocumentDetailResponse = {
  data: Document;
};

type DocumentPlanLinkListResponse = {
  data: DocumentPlanLink[];
};

type DocumentShareStatusResponse = {
  data: DocumentShareStatus;
};

type PublicSharedDocumentResponse = {
  data: PublicSharedDocument;
};

type DocumentAccessGrantResponse = {
  data: DocumentAccessGrant;
};

type DocumentAccessGrantListResponse = {
  data: DocumentAccessGrant[];
  meta: PaginationMeta;
};

type DocumentRevisionListResponse = {
  data: DocumentRevision[];
  meta: PaginationMeta;
};

type DocumentRevisionDetailResponse = {
  data: DocumentRevisionDetail;
};

type DocumentPagedResponse = {
  data: DocumentListItem[];
  meta: PaginationMeta;
};

const buildDocumentsUrl = (projectId: string, params?: DocumentQueryParams) => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.tags && params.tags.length > 0) queryParams.set('tags', params.tags.join(','));
  if (params?.tagPrefix) queryParams.set('tagPrefix', params.tagPrefix);
  if (params?.untagged) queryParams.set('untagged', 'true');
  if (params?.favorite) queryParams.set('favorite', 'true');
  if (params?.sharedByMe) queryParams.set('sharedByMe', 'true');
  if (params?.visibility) queryParams.set('visibility', params.visibility);
  if (params?.archived && params.archived !== 'ACTIVE') queryParams.set('archived', params.archived);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  return `${getApiUrl()}/api/projects/${projectId}/documents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
};

export const fetchDocumentsPaged = async (
  token: string,
  projectId: string,
  params: DocumentQueryParams = {},
): Promise<PagedData<DocumentListItem>> => {
  const response = await fetchWithAuthRetry(buildDocumentsUrl(projectId, params), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch documents: ${response.status}`);
  }

  return (await response.json()) as DocumentPagedResponse;
};

const buildDocumentTagsUrl = (projectId: string, params?: DocumentTagsQueryParams) => {
  const queryParams = new URLSearchParams();
  if (params?.visibility) queryParams.set('visibility', params.visibility);
  if (params?.archived && params.archived !== 'ACTIVE') queryParams.set('archived', params.archived);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);

  return `${getApiUrl()}/api/projects/${projectId}/documents/tags${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
};

export const fetchDocumentTags = async (
  token: string,
  projectId: string,
  params: DocumentTagsQueryParams = {},
): Promise<DocumentTagAggregate[]> => {
  const response = await fetchWithAuthRetry(buildDocumentTagsUrl(projectId, params), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document tags: ${response.status}`);
  }

  const data = (await response.json()) as { data: DocumentTagAggregate[] };
  return data.data;
};

export const fetchDocumentTagSuggestions = async (
  token: string,
  projectId: string,
  params: DocumentTagsQueryParams = {},
): Promise<DocumentTagSuggestions> => {
  const queryParams = new URLSearchParams();
  if (params.visibility) queryParams.set('visibility', params.visibility);
  if (params.archived && params.archived !== 'ACTIVE') queryParams.set('archived', params.archived);
  const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/tags/suggestions${query}`,
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch tag suggestions: ${response.status}`);
  }

  const data = (await response.json()) as { data: DocumentTagSuggestions };
  return data.data;
};

export const renameDocumentTag = async (
  token: string,
  projectId: string,
  input: RenameDocumentTagInput,
): Promise<{ updatedDocumentCount: number }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/tags/rename`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to rename tag: ${response.status}`);
  }

  const data = (await response.json()) as { data: { updatedDocumentCount: number } };
  return data.data;
};

// AI relabel 제안 조회: LLM 호출이라 비용이 드는 on-demand 경로(사용자 버튼 클릭 시에만 호출). 적용은 하지 않는다.
// 프로바이더 미설정 시 422(errorCode: LLM_PROVIDER_NOT_CONFIGURED)로 응답하므로 호출부에서 구분해 안내한다.
export const fetchDocumentTagRelabelSuggestions = async (
  token: string,
  projectId: string,
): Promise<DocumentTagRelabelSuggestion[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/tags/relabel-suggestions`,
    { method: 'POST', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch tag relabel suggestions: ${response.status}`);
  }

  const data = (await response.json()) as { data: { ops: DocumentTagRelabelSuggestion[] } };
  return data.data.ops;
};

// 사람이 수락한 relabel op을 단일 트랜잭션으로 일괄 적용한다(감사 로그 포함).
export const applyDocumentTagRelabel = async (
  token: string,
  projectId: string,
  ops: DocumentTagRelabelApplyOp[],
): Promise<DocumentTagRelabelApplyResponse> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/tags/relabel/apply`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ ops }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to apply tag relabel: ${response.status}`);
  }

  const data = (await response.json()) as { data: DocumentTagRelabelApplyResponse };
  return data.data;
};

export const deleteDocumentTag = async (
  token: string,
  projectId: string,
  input: { tag: string; includeDescendants?: boolean },
): Promise<{ updatedDocumentCount: number }> => {
  const params = new URLSearchParams({ tag: input.tag });
  if (input.includeDescendants !== undefined) params.set('includeDescendants', String(input.includeDescendants));

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/tags?${params.toString()}`,
    { method: 'DELETE', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete tag: ${response.status}`);
  }

  const data = (await response.json()) as { data: { updatedDocumentCount: number } };
  return data.data;
};

export const promoteSuggestedTag = async (
  token: string,
  projectId: string,
  documentId: string,
  tag: string,
): Promise<Document> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/suggested-tags/promote`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ tag }),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to promote suggested tag: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const dismissSuggestedTag = async (
  token: string,
  projectId: string,
  documentId: string,
  tag: string,
): Promise<Document> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/suggested-tags/dismiss`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ tag }),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to dismiss suggested tag: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const fetchDocumentById = async (token: string, projectId: string, documentId: string): Promise<Document> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const fetchDocumentPlans = async (
  token: string,
  projectId: string,
  documentId: string,
): Promise<DocumentPlanLink[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/plans`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document plans: ${response.status}`);
  }

  const data = (await response.json()) as DocumentPlanLinkListResponse;
  return data.data;
};

export const fetchDocumentMarkdown = async (token: string, projectId: string, documentId: string): Promise<string> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/download`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to download document: ${response.status}`);
  }

  return response.text();
};

export const fetchDocumentShareStatus = async (
  token: string,
  projectId: string,
  documentId: string,
): Promise<DocumentShareStatus> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/share`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document share status: ${response.status}`);
  }

  const data = (await response.json()) as DocumentShareStatusResponse;
  return data.data;
};

export const createDocumentShare = async (
  token: string,
  projectId: string,
  documentId: string,
): Promise<DocumentShareStatus> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/share`, {
    method: 'POST',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create document share: ${response.status}`);
  }

  const data = (await response.json()) as DocumentShareStatusResponse;
  return data.data;
};

export const revokeDocumentShare = async (
  token: string,
  projectId: string,
  documentId: string,
): Promise<DocumentShareStatus> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/share`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to revoke document share: ${response.status}`);
  }

  const data = (await response.json()) as DocumentShareStatusResponse;
  return data.data;
};

// 서버 pageSize 상한(100)으로 한 번에 받는다. 한 문서를 100명 넘게 개별 공유하는 경우는 상정하지 않는다.
const DOCUMENT_ACCESS_GRANT_PAGE_SIZE = 100;

export const fetchDocumentAccessGrants = async (
  token: string,
  projectId: string,
  documentId: string,
): Promise<DocumentAccessGrant[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/access-grants?pageSize=${DOCUMENT_ACCESS_GRANT_PAGE_SIZE}`,
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document access grants: ${response.status}`);
  }

  const data = (await response.json()) as DocumentAccessGrantListResponse;
  return data.data;
};

export const createDocumentAccessGrant = async (
  token: string,
  projectId: string,
  documentId: string,
  memberId: string,
): Promise<DocumentAccessGrant> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/access-grants`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ memberId }),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to grant document access: ${response.status}`);
  }

  const data = (await response.json()) as DocumentAccessGrantResponse;
  return data.data;
};

export const revokeDocumentAccessGrant = async (
  token: string,
  projectId: string,
  documentId: string,
  grantId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/access-grants/${grantId}`,
    { method: 'DELETE', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to revoke document access grant: ${response.status}`);
  }
};

export const fetchPublicSharedDocument = async (shareToken: string): Promise<PublicSharedDocument> => {
  const response = await fetchWithTimeout(`${getApiUrl()}/api/share/documents/${encodeURIComponent(shareToken)}`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch shared document: ${response.status}`, {
      skipRedirectOn401: true,
    });
  }

  const data = (await response.json()) as PublicSharedDocumentResponse;
  return data.data;
};

export const createDocument = async (
  token: string,
  projectId: string,
  input: CreateDocumentInput,
): Promise<Document> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create document: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const updateDocument = async (
  token: string,
  projectId: string,
  documentId: string,
  input: UpdateDocumentInput,
): Promise<Document> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update document: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const deleteDocument = async (token: string, projectId: string, documentId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/documents/${documentId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete document: ${response.status}`);
  }
};

export const archiveDocument = async (token: string, projectId: string, documentId: string): Promise<Document> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/archive`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to archive document: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const unarchiveDocument = async (token: string, projectId: string, documentId: string): Promise<Document> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/unarchive`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unarchive document: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};

export const toggleDocumentFavorite = async (
  token: string,
  projectId: string,
  documentId: string,
  isFavorite: boolean,
): Promise<boolean> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/favorite`,
    {
      method: 'PUT',
      headers: authHeaders(token, true),
      body: JSON.stringify({ isFavorite }),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to toggle document favorite: ${response.status}`);
  }

  const data = (await response.json()) as { data: { isFavorite: boolean } };
  return data.data.isFavorite;
};

export const fetchDocumentRevisions = async (
  token: string,
  projectId: string,
  documentId: string,
): Promise<DocumentRevision[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/revisions`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document revisions: ${response.status}`);
  }

  const data = (await response.json()) as DocumentRevisionListResponse;
  return data.data;
};

export const fetchDocumentRevisionById = async (
  token: string,
  projectId: string,
  documentId: string,
  revisionId: string,
): Promise<DocumentRevisionDetail> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/revisions/${revisionId}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document revision: ${response.status}`);
  }

  const data = (await response.json()) as DocumentRevisionDetailResponse;
  return data.data;
};

export const restoreDocumentRevision = async (
  token: string,
  projectId: string,
  documentId: string,
  revisionId: string,
): Promise<Document> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/restore`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to restore document revision: ${response.status}`);
  }

  const data = (await response.json()) as DocumentDetailResponse;
  return data.data;
};
